import { LoginActions } from "components/SideBar/slice/index";
import { store } from "./@reduxjs/store";
import hmacSHA512 from "crypto-js/hmac-sha512";
import Base64 from "crypto-js/enc-base64";

/**
 * Parses the JSON returned by a network request.
 *
 * @param {Response} response - The fetch Response object.
 * @return {Promise<any|null>} Parsed JSON object, or null for empty responses.
 */
function parseJSON(response) {
  if (response.status === 204 || response.status === 205) {
    return null;
  }
  return response.json();
}

/**
 * Checks the HTTP response:
 * - Allows 2xx responses
 * - Handles "unauthorized" and "access_denied" based on `error` field in body
 * - Throws for all other errors
 *
 * @param {Response} response - The fetch Response object.
 * @return {Promise<Response>} The validated response if successful.
 * @throws {Error} If the response is not valid.
 */
async function checkStatus(response) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }

  const clonedResponse = response.clone();
  let body = {};

  try {
    body = await clonedResponse.json();
  } catch {
    // fallback if body is not JSON
  }

  const statusCode = response.status;
  const errorType = body?.error;
  const errorMessage =
    body?.error ||
    body?.error_message ||
    body?.message ||
    body?.detail ||
    `HTTP error: ${statusCode}`;

  // 🔹 Handle Unauthorized or Not Logged In
  if (
    statusCode === 401 ||
    ["unauthorized", "not_logged_in"].includes(errorType)
  ) {
    store.dispatch(LoginActions.logoutRequest({ navigate: () => {} }));
    localStorage.clear();

    if (!window.location.pathname.includes("login")) {
      window.location.href = "/login";
    }

    return Promise.reject(new Error(errorMessage || "Unauthorized")); // <-- preserve backend message
  }

  // 🔹 Handle Forbidden (403)
  if (statusCode === 403 || errorType === "access_denied") {
    return Promise.reject(new Error("Access denied"));
  }

  // 🔹 Other errors
  const error = new Error(errorMessage);
  error.response = response;
  throw error;
}

/**
 * Main API request utility using fetch.
 *
 * @param {string} url - The URL or path for the request.
 * @param {Object} options - Fetch options (method, headers, body, etc).
 * @return {Promise<any|null>} The parsed JSON response or null.
 */
export default async function request(url, options) {
  const requestUrl = url;

  const fetchOptions = {
    credentials: "include", // ensures cookies are sent
    ...options,
    headers: {
      ...(options.headers || {})
      // no Authorization header needed
    }
  };

  const response = await fetch(requestUrl, fetchOptions);
  const validatedResponse = await checkStatus(response);
  return parseJSON(validatedResponse);
}

const BASE_URL = process.env.REACT_APP_HASHFAME_API_URL;
// REACT_APP_HASHFAME_API_URL=https://staging3.hashfame.com

export async function hashfameRequest(url, options = {}) {
  const requestUrl = `${BASE_URL}${url}`;

  const fetchOptions = {
    ...options,
    headers: {
      ...(options.headers || {})
    }
  };

  const response = await fetch(requestUrl, fetchOptions);
  const validatedResponse = await checkStatus(response);
  return parseJSON(validatedResponse);
}

export function generateSignature(method, url, body) {
  const secret = process.env.REACT_APP_HMAC_SHA_KEY;
  const signature_data = `${method}${url}${body}`;
  //

  const signature = Base64.stringify(hmacSHA512(signature_data, secret));
  return signature;
}
